// KBC theme provider.
// Single source of truth for light/dark: sets <html data-theme> (drives CSS-variable
// tokens / Tailwind) and builds a matching MUI theme for the admin screens.
// Persisted to localStorage 'kbc-theme'. Default is light.
import React, { createContext, useState, useContext, useMemo, useEffect, useCallback } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";

export const ThemeContext = createContext({ mode: "light", toggleTheme: () => {}, setMode: () => {} });
export const useCustomTheme = () => useContext(ThemeContext);

const STORAGE_KEY = "kbc-theme";

const PALETTES = {
  light: {
    // `primary` is a fill that carries white text, so it must clear 4.5:1.
    // #0B7A55 measures 5.34:1 against white. The bright brand green
    // (#06C281) is only 2.32:1 and is kept as `accentBrand` for borders,
    // rails, icons and chart series — never behind text.
    primary: "#0B7A55", primaryHi: "#086345", accentBrand: "#06C281", accent: "#11A6B6",
    bgDefault: "#F7F9F8", paper: "#FFFFFF",
    ink: "#16241F", text: "#4A5C55", muted: "#71837B",
    divider: "rgba(15,50,40,0.12)", onPrimary: "#FFFFFF",
  },
  dark: {
    // On dark the bright green carries dark text (#04221a), which passes.
    primary: "#29F5A6", primaryHi: "#19E8C0", accentBrand: "#29F5A6", accent: "#34E0E0",
    bgDefault: "#0F1714", paper: "#16211C",
    ink: "#E4EDE9", text: "#A0B0A9", muted: "#7A8C84",
    divider: "rgba(255,255,255,0.10)", onPrimary: "#04221a",
  },
};

const getInitialMode = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
};

export const CustomThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(getInitialMode);

  useEffect(() => {
    try {
      document.documentElement.setAttribute("data-theme", mode);
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* no-op */
    }
  }, [mode]);

  const toggleTheme = useCallback(() => setMode((p) => (p === "dark" ? "light" : "dark")), []);

  const theme = useMemo(() => {
    const p = PALETTES[mode];
    return createTheme({
      palette: {
        mode,
        primary: { main: p.primary, light: p.accentBrand, dark: p.primaryHi, contrastText: p.onPrimary },
        secondary: { main: p.accent },
        success: { main: p.primary },
        warning: { main: mode === "dark" ? "#FFC15A" : "#A5670B" },
        error: { main: mode === "dark" ? "#f07878" : "#be2828" },
        info: { main: mode === "dark" ? "#7FB8E6" : "#2C77B8" },
        background: { default: p.bgDefault, paper: p.paper },
        text: { primary: p.ink, secondary: p.text },
        divider: p.divider,
      },
      shape: { borderRadius: 6 },
      // Compact scale, matching the Tailwind fontSize remap so MUI admin
      // screens and Tailwind member pages land on the same rhythm.
      typography: {
        fontFamily: '"Inter", system-ui, "Helvetica", "Arial", sans-serif',
        fontSize: 13.5,
        h1: { fontFamily: '"Space Grotesk", sans-serif', fontSize: "34px", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.15 },
        h2: { fontFamily: '"Space Grotesk", sans-serif', fontSize: "30px", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.2 },
        h3: { fontFamily: '"Space Grotesk", sans-serif', fontSize: "26px", fontWeight: 700, letterSpacing: "-0.015em", lineHeight: 1.25 },
        h4: { fontFamily: '"Space Grotesk", sans-serif', fontSize: "20px", fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.3 },
        h5: { fontFamily: '"Space Grotesk", sans-serif', fontSize: "18px", fontWeight: 600, lineHeight: 1.35 },
        h6: { fontFamily: '"Space Grotesk", sans-serif', fontSize: "16.5px", fontWeight: 600, lineHeight: 1.4 },
        button: { textTransform: "none", fontWeight: 600 },
        body1: { fontSize: "15px", lineHeight: 1.5 },
        body2: { fontSize: "13.25px", lineHeight: 1.5 },
      },
      components: {
        MuiPaper: {
          defaultProps: { elevation: 0 },
          styleOverrides: { root: { backgroundImage: "none" } },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 8,
              border: `1px solid ${p.divider}`,
              boxShadow: "none",
              backgroundImage: "none",
            },
          },
        },
        MuiCardContent: { styleOverrides: { root: { padding: 14, "&:last-child": { paddingBottom: 14 } } } },
        MuiButton: {
          defaultProps: { disableElevation: true, size: "small" },
          styleOverrides: {
            root: { borderRadius: 6, fontWeight: 600, fontFamily: '"Space Grotesk", sans-serif', padding: "6px 12px" },
            containedPrimary: {
              color: p.onPrimary,
              boxShadow: "none",
              "&:hover": { backgroundColor: p.primaryHi, boxShadow: "none" },
            },
            outlined: { borderColor: p.divider },
          },
        },
        MuiTextField: { defaultProps: { variant: "outlined", fullWidth: true, size: "small" } },
        MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 6 } } },
        MuiSelect: { defaultProps: { size: "small" } },
        MuiChip: { defaultProps: { size: "small" }, styleOverrides: { root: { fontWeight: 600, borderRadius: 4 } } },
        MuiTable: { defaultProps: { size: "small" } },
        MuiTableCell: { styleOverrides: { head: { fontWeight: 700, color: p.ink } } },
        MuiDialog: { styleOverrides: { paper: { borderRadius: 8, backgroundImage: "none" } } },
        MuiTooltip: { styleOverrides: { tooltip: { borderRadius: 4, fontSize: "12px" } } },
      },
    });
  }, [mode]);

  const value = useMemo(() => ({ mode, toggleTheme, setMode }), [mode, toggleTheme]);

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default CustomThemeProvider;
